import { getApi, postApi, putApi, delApi } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";
import {
  IntegrationStatus,
  IntegrationBase,
  CreateIntegrationBaseData,
  CreateIntegrationBaseResult,
  IntegrationPostInstall,
  UpdateIntegrationBaseData,
  IntegrationClassification,
  IntegrationDependencyError,
  McpInspectRequest,
  McpInspectResult,
  AddMcpServerRequest,
  AddMcpServerResult,
  McpRefreshPreview,
  ToolsDiff,
  UpdateIntegrationToolsData,
  IntegrationUsage,
} from "../types/integration";

/**
 * Escopa UMA chamada numa organizacao que nao e a ativa da sessao (AB#85605).
 *
 * O header da organizacao ativa e injetado pelo `fetchApi` a partir do armazenamento local;
 * os headers passados por chamada vencem esse default. Sem o parametro, nada muda.
 */
function organizationHeader(organizationId?: string) {
  return organizationId ? { headers: { "X-Organization-Id": organizationId } } : {};
}

/**
 * Classe de serviço para operações relacionadas às integrações base (integrações configuradas)
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class IntegrationBaseService {
  private static readonly BASE_URL = "/integration-bases";

  /**
   * Obtém todas as integrações base com filtros opcionais
   * @param params Parâmetros de filtro
   * @returns Lista de integrações base
   */
  static async getAll(params?: {
    product_id?: string;
    integration_id?: string;
    classification?: IntegrationClassification;
    status?: IntegrationStatus;
    scope?: "local" | "global";
    search?: string;
    /**
     * Organizacao alvo, quando ela NAO e a ativa da sessao (AB#85605).
     *
     * A tela de modelos de IA da plataforma lista as integracoes da organizacao da
     * plataforma, e o superadmin que a abre esta em escopo de plataforma — sem organizacao
     * ativa, o backend cairia na organizacao do perfil dele e devolveria outra lista.
     */
    organization_id?: string;
    /**
     * So as integracoes que declaram a capacidade em `metadata.capabilities` (AB#86885).
     * A busca web escolhe entre elas, e a Tavily nao e `classification: llm`.
     */
    capability?: "web_search";
  }): Promise<IntegrationBase[]> {
    try {
      // Sem `organization_id` nem `with_template` na assinatura: a organização vem
      // do token e o template é buscado à parte. Enquanto estavam aqui, quatro
      // chamadores mandavam `organization_id` e o filtro nunca ia na query —
      // parâmetro aceito e ignorado parece filtro aplicado.
      // product_id → workspace scope: v2 retorna globais + locais vinculadas ao produto
      // (com shadow dedup), parity com o legacy. Forward dos demais filtros que v2 entende.
      const v2Params = new URLSearchParams();
      if (params?.status) v2Params.append("status", params.status);
      if (params?.classification)
        v2Params.append("classification", params.classification);
      if (params?.integration_id)
        v2Params.append("integration_id", params.integration_id);
      if (params?.search) v2Params.append("search", params.search);
      if (params?.capability) v2Params.append("capability", params.capability);
      if (params?.scope) v2Params.append("scope", params.scope);
      if (params?.product_id) v2Params.append("product_id", params.product_id);
      const qs = v2Params.toString();
      const url = qs
        ? `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}?${qs}`
        : `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}`;
      return await getApi<IntegrationBase[]>(url, organizationHeader(params?.organization_id));
    } catch (error) {
      console.error("Erro ao buscar integrações base:", error);
      throw error;
    }
  }

  /**
   * Obtém uma integração base pelo ID
   * @param id ID da integração base
   * @returns Dados da integração base
   */
  static async getById(id: string, organizationId?: string): Promise<IntegrationBase> {
    try {
      // Sem join inline de template: quem precisa do template busca separado.
      return await getApi<IntegrationBase>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`,
        organizationHeader(organizationId),
      );
    } catch (error) {
      console.error(`Erro ao buscar integração base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Cria uma nova integração base
   * @param data Dados da integração base a ser criada
   * @returns Integração base criada e, quando o template sinaliza via
   *          settings.post_install.emit_webhook, também um objeto post_install
   *          com a URL do webhook e as instruções de setup para exibição em modal.
   */
  static async create(
    data: CreateIntegrationBaseData,
    organizationId?: string,
  ): Promise<CreateIntegrationBaseResult> {
    try {
      const body = this.buildCreateBody(data);
      const result = await postApi<{
        integration: IntegrationBase;
        post_install?: IntegrationPostInstall;
      }>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}`,
        body,
        organizationHeader(organizationId),
      );
      return {
        integration: result.integration,
        post_install: result.post_install,
      };
    } catch (error) {
      console.error("Erro ao criar integração base:", error);
      throw error;
    }
  }

  /**
   * Atualiza uma integração base existente
   * @param id ID da integração base
   * @param data Dados a serem atualizados
   * @returns Integração base atualizada e, quando o template sinaliza via
   *          settings.post_install, também o objeto post_install com endpoints,
   *          manifest e instruções para exibição no modal pós-edição.
   */
  static async update(
    id: string,
    data: UpdateIntegrationBaseData,
    organizationId?: string,
  ): Promise<{
    integration: IntegrationBase;
    post_install?: IntegrationPostInstall;
  }> {
    try {
      const body = this.buildUpdateBody(data);
      const result = await putApi<{
        integration: IntegrationBase;
        post_install?: IntegrationPostInstall;
      }>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`,
        body,
        organizationHeader(organizationId),
      );
      return {
        integration: result.integration,
        post_install: result.post_install,
      };
    } catch (error) {
      console.error(`Erro ao atualizar integração base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remove uma integração base
   * @param id ID da integração base
   * @returns Verdadeiro se a remoção foi bem-sucedida
   * @throws IntegrationDependencyError quando a integração possui dependências (apenas legacy; v2 deixa FK do Postgres falhar)
   */
  static async delete(id: string, organizationId?: string): Promise<void> {
    try {
      await delApi(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`,
        organizationHeader(organizationId),
      );
      return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(`Erro ao remover integração base com ID ${id}:`, error);

      // Verificar se é um erro 400 com dependências (usar response.data se data estiver null)
      const errorData = error.data || error.response?.data;
      if (error.status === 400 && errorData?.dependencies) {
        throw new IntegrationDependencyError(errorData);
      }

      throw error;
    }
  }

  /**
   * Constrói body aceito pelo v2 `POST /integration-bases`.
   *
   * v2 valida: `integration_id`, `label`, `description?`, `status?`, `scope?`,
   * `product_id?`, `settings?`, `mcp_config?`, `credentials?` (record string→string).
   * Tudo o mais é derivado do template (`name`, `auth_type`, `auth_fields`,
   * `logo_url`, `url`, `classification`) ou da identidade autenticada
   * (`organization_id`, `created_by`). `title` não existe em v2.
   */
  private static buildCreateBody(data: CreateIntegrationBaseData) {
    // `product_id` deriva scope 'local' no v2 e cria o vínculo em
    // integrations_product (parity legacy). Sem ele, scope explícito do caller
    // (default v2 = 'global').
    return {
      integration_id: data.integration_id,
      label: data.label,
      ...(data.title !== undefined ? { title: data.title } : {}),
      ...(data.metadata !== undefined ? { metadata: data.metadata } : {}),
      ...(data.description !== undefined
        ? { description: data.description }
        : {}),
      ...(data.status !== undefined ? { status: data.status } : {}),
      ...(data.scope !== undefined ? { scope: data.scope } : {}),
      ...(data.product_id !== undefined ? { product_id: data.product_id } : {}),
      ...(data.settings !== undefined ? { settings: data.settings } : {}),
      ...(data.mcp_config !== undefined ? { mcp_config: data.mcp_config } : {}),
      ...(data.credentials !== undefined
        ? { credentials: this.coerceCredentials(data.credentials) }
        : {}),
    };
  }

  /**
   * Constrói body aceito pelo v2 `PUT /integration-bases/:id`. Mesma forma do
   * create sem `integration_id` (não muta após criação). `scope` + `product_id`
   * juntos gerenciam o vínculo de produto na mudança de escopo (parity legacy).
   */
  private static buildUpdateBody(data: UpdateIntegrationBaseData) {
    return {
      ...(data.label !== undefined ? { label: data.label } : {}),
      ...(data.title !== undefined ? { title: data.title } : {}),
      ...(data.metadata !== undefined ? { metadata: data.metadata } : {}),
      ...(data.description !== undefined
        ? { description: data.description }
        : {}),
      ...(data.status !== undefined ? { status: data.status } : {}),
      ...(data.scope !== undefined ? { scope: data.scope } : {}),
      ...(data.product_id !== undefined ? { product_id: data.product_id } : {}),
      ...(data.settings !== undefined ? { settings: data.settings } : {}),
      ...(data.mcp_config !== undefined ? { mcp_config: data.mcp_config } : {}),
      ...(data.credentials !== undefined
        ? { credentials: this.coerceCredentials(data.credentials) }
        : {}),
    };
  }

  /**
   * v2 valida `credentials` como `Record<string, string>`. Coerce valores não-string
   * preservando keys; descarta undefined/null.
   */
  private static coerceCredentials(
    creds: Record<string, unknown>,
  ): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(creds)) {
      if (value === undefined || value === null) continue;
      out[key] = typeof value === "string" ? value : String(value);
    }
    return out;
  }

  /**
   * Obtém integrações base ativas — organização derivada do token.
   * @param workspaceId Se informado, aplica escopo: retorna globais + locais vinculadas ao produto
   * @returns Lista de integrações base ativas
   */
  static async getActive(workspaceId?: string): Promise<IntegrationBase[]> {
    return this.getAll({
      status: "active",
      ...(workspaceId ? { product_id: workspaceId } : {}),
    });
  }

  // ─── Servidores MCP remotos (AB#87477) ─────────────────────────────────────

  /**
   * Conecta no servidor MCP sem gravar nada: diz como ele autentica, se já existe template
   * com a mesma URL e, quando dá para listar, as tools que ele oferece.
   */
  static async inspectMcpServer(
    data: McpInspectRequest,
    organizationId?: string,
  ): Promise<McpInspectResult> {
    return postApi<McpInspectResult>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/mcp/inspect`,
      data,
      organizationHeader(organizationId),
    );
  }

  /**
   * Cria (ou reaproveita, pela `server_url`) o template MCP e instala a instância.
   * Com OAuth, a instância nasce `pending` e `oauth_required` vem `true`. Resposta no
   * envelope do `POST /integration-bases`: `{ integration, post_install, oauth_required }`.
   */
  static async addMcpServer(
    data: AddMcpServerRequest,
    organizationId?: string,
  ): Promise<AddMcpServerResult> {
    return postApi<AddMcpServerResult>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/mcp`,
      data,
      organizationHeader(organizationId),
    );
  }

  /** URL de autorização do provedor OAuth; quem chama redireciona o navegador para ela. */
  static async startMcpOAuth(id: string, organizationId?: string): Promise<string> {
    const result = await postApi<{ authorization_url: string }>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/mcp/oauth/start`,
      undefined,
      organizationHeader(organizationId),
    );
    return result.authorization_url;
  }

  /** Prévia do que mudou no servidor desde o snapshot, com as skills afetadas — nada é gravado. */
  static async previewMcpRefresh(id: string, organizationId?: string): Promise<McpRefreshPreview> {
    return getApi<McpRefreshPreview>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/mcp/refresh`,
      organizationHeader(organizationId),
    );
  }

  /**
   * Aceita o diff do servidor: o snapshot da instância passa a ser o atual. Com o `toolsHash`
   * da prévia, a api recusa (`E_MCP_TOOLS_CHANGED`) se o servidor mudou a lista desde então.
   */
  static async applyMcpRefresh(
    id: string,
    toolsHash?: string,
    organizationId?: string,
  ): Promise<IntegrationBase> {
    return postApi<IntegrationBase>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/mcp/refresh`,
      toolsHash ? { tools_hash: toolsHash } : undefined,
      organizationHeader(organizationId),
    );
  }

  /** O title identifica a instalação e é único na organização, sem diferenciar caixa (AB#87541). */
  static async isTitleAvailable(
    title: string,
    excludeId?: string,
    organizationId?: string,
  ): Promise<boolean> {
    const params = new URLSearchParams({ title });
    if (excludeId) params.append("exclude_id", excludeId);
    const result = await getApi<{ available: boolean }>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/title-available?${params.toString()}`,
      organizationHeader(organizationId),
    );
    return result.available;
  }

  // ─── Página da integração: tools, template e uso (AB#87542) ────────────────

  /** Estado por tool e/ou a lista de tools do conector; a api devolve a instância. */
  static async updateTools(
    id: string,
    data: UpdateIntegrationToolsData,
    organizationId?: string,
  ): Promise<IntegrationBase> {
    return putApi<IntegrationBase>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/tools`,
      data,
      organizationHeader(organizationId),
    );
  }

  /** Instalação × template atual — nada é gravado. */
  static async getTemplateDiff(id: string, organizationId?: string): Promise<ToolsDiff> {
    return getApi<ToolsDiff>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/template-diff`,
      organizationHeader(organizationId),
    );
  }

  /** Traz do template as tools escolhidas (por nome), preservando os estados. */
  static async applyTemplateDiff(
    id: string,
    tools: string[],
    organizationId?: string,
  ): Promise<IntegrationBase> {
    return postApi<IntegrationBase>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/template-diff/apply`,
      { tools },
      organizationHeader(organizationId),
    );
  }

  /** Chamadas recentes e contagem por tool. */
  static async getUsage(id: string, limit = 50, organizationId?: string): Promise<IntegrationUsage> {
    const params = new URLSearchParams({ limit: String(limit) });
    return getApi<IntegrationUsage>(
      `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}/usage?${params.toString()}`,
      organizationHeader(organizationId),
    );
  }
}
